import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { cn } from '@src/design-system/helpers';
import { CloseCircle } from 'iconsax-react';

type Item = {
  name: string;
  avatar: string;
  id: number;
};

type SelectedItemsProps = {
  items: Item[];
  onClose?: () => void;
  className?: string;
  maxDisplayed?: number;
  avatarClassName?: string;
  showAvatarsOnly?: boolean;
};

const MAX_DISPLAYED = 3;

export const SelectedItems = ({
  items = [],
  onClose,
  className,
  maxDisplayed = MAX_DISPLAYED,
  avatarClassName,
  showAvatarsOnly,
}: SelectedItemsProps) => {
  const isSingle = items.length === 1;
  const shouldShowAvatarsOnly = !isSingle || showAvatarsOnly;

  const containerClasses = cn(
    'group transition-all flex border border-transparent hover:border hover:border-pale-border rounded-xl p-2 gap-1 text-xs justify-center items-center',
    !isSingle && 'cursor-default',
    className
  );

  if (shouldShowAvatarsOnly) {
    const displayedItems = items.slice(0, maxDisplayed);
    const restCount = Math.max(items.length - maxDisplayed, 0);

    return (
      <div className={containerClasses}>
        {displayedItems.map((item) => (
          <Avatar
            key={item.id}
            className={cn(
              'first:ms-0 -ms-3 border-2 border-white size-6',
              avatarClassName
            )}
          >
            <AvatarImage src={item.avatar} />
            <AvatarFallback colorId={item.id}>{item.name[0]}</AvatarFallback>
          </Avatar>
        ))}

        {restCount > 0 && (
          <span className='text-gray-600 text-xs'>+{restCount}</span>
        )}
      </div>
    );
  }

  const { avatar, id, name } = items[0];

  return (
    <div className={containerClasses}>
      <Avatar
        className={cn('size-6 border-2 border-transparent', avatarClassName)}
      >
        <AvatarImage src={avatar} />
        <AvatarFallback colorId={id}>{name?.[0]}</AvatarFallback>
      </Avatar>

      <span>{name}</span>

      {onClose && (
        <CloseCircle
          size={16}
          onClick={onClose}
          className='opacity-0 transition-all invisible group-hover:opacity-70 group-hover:visible shrink-0 hover:opacity-100 cursor-pointer'
        />
      )}
    </div>
  );
};
